import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import BusinessIcon from '@mui/icons-material/Business'
import PaymentsIcon from '@mui/icons-material/Payments'
import InsightsIcon from '@mui/icons-material/Insights'
import EventIcon from '@mui/icons-material/Event'
import HistoryIcon from '@mui/icons-material/History'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import EmailIcon from '@mui/icons-material/Email'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import BuildIcon from '@mui/icons-material/Build'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import CampaignIcon from '@mui/icons-material/Campaign'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import API from '../../services/API'
import PageHeader from '../kit/PageHeader'
import StatTile from '../kit/StatTile'
import SectionCard from '../kit/SectionCard'
import { DemoChip, SoftChip, StageChip, TrialledChip } from '../kit/Chips'
import {
  TIER_NAMES,
  followUpState,
  formatCurrency,
  formatDate,
  formatNumber,
  formatPeriod,
  fromNow,
  stageMeta,
} from '../kit/helpers'
import { ACTION_STATUS } from '../Tenants/CommercialUsage/constants'

// "PTS journeys 612 of 500 · control-room hours 231.5 of 200" for the metrics a
// commercial usage row exceeded. The figures are the server's.
const usageFigures = (row) => {
  const exceeded = row.exceededMetrics ?? []
  const hours = (value) => formatNumber(value, { maximumFractionDigits: 1 })
  return [
    exceeded.includes('ptsJourneysPerMonth')
      ? `PTS journeys ${formatNumber(row.ptsCount)} of ${formatNumber(row.ptsAllowance)}`
      : '',
    exceeded.includes('controlRoomHoursPerMonth')
      ? `control-room hours ${hours(row.controlRoomHours)} of ${hours(row.controlRoomAllowanceHours)}`
      : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

// A finalised month whose manual review action is still open.
const usageActionRow = (action) => ({
  key: `usage-${action.tenantId}-${action.period}`,
  tone: action.priority === 'high' ? '#E32525' : '#85276A',
  title: `${action.name} - ${ACTION_STATUS[action.status]?.plain ?? 'commercial usage action required'}`,
  detail: [
    formatPeriod(action.period),
    action.occurrenceNumber ? `occurrence ${action.occurrenceNumber}` : '',
    usageFigures(action),
  ]
    .filter(Boolean)
    .join(' · '),
  badge:
    action.priority === 'high' ? (
      <SoftChip color="#E32525" label="High priority" sx={{ height: 20, fontSize: 11, flexShrink: 0 }} />
    ) : null,
  to: `/tenants/${action.tenantId}?tab=usage`,
})

const AttentionRow = ({ tone = '#F28B1F', title, detail, badge, icon, onOpen }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1.5}
    sx={{ px: 2, py: 1.25, borderBottom: '1px solid #EEF2F7', '&:last-of-type': { borderBottom: 0 } }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 2,
        bgcolor: alpha(tone, 0.12),
        color: tone,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon ?? <WarningAmberIcon sx={{ fontSize: 18 }} />}
    </Box>
    <Box minWidth={0} flex={1}>
      <Stack direction="row" gap={0.75} alignItems="center" minWidth={0}>
        <Typography variant="body2" fontWeight={600} color="text.primary" noWrap={!badge}>
          {title}
        </Typography>
        {badge}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {detail}
      </Typography>
    </Box>
    <Button size="small" onClick={onOpen} endIcon={<ChevronRightIcon />}>
      Open
    </Button>
  </Stack>
)

const LeadRow = ({ lead, onOpen, trailing }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1.5}
    component="button"
    type="button"
    onClick={onOpen}
    sx={{
      width: '100%',
      textAlign: 'left',
      border: 0,
      bgcolor: 'transparent',
      cursor: 'pointer',
      fontFamily: 'inherit',
      px: 2,
      py: 1.25,
      borderBottom: '1px solid #EEF2F7',
      '&:last-of-type': { borderBottom: 0 },
      '&:hover': { bgcolor: '#F8FAFC' },
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: stageMeta(lead.stage).color,
        flexShrink: 0,
      }}
    />
    <Box minWidth={0} flex={1}>
      <Stack direction="row" gap={0.75} alignItems="center" minWidth={0}>
        <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
          {lead.organisationName}
        </Typography>
        {lead.demoAt && <DemoChip sx={{ height: 20, fontSize: 11 }} />}
        {lead.trialled && <TrialledChip sx={{ height: 20, fontSize: 11 }} />}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>
        {[lead.contactName, lead.source].filter(Boolean).join(' · ') || 'No contact recorded'}
      </Typography>
    </Box>
    {trailing}
  </Stack>
)

const EmptyRow = ({ icon, text }) => (
  <Stack direction="row" gap={1} alignItems="center" px={2} py={2} color="text.secondary">
    {icon}
    <Typography variant="body2">{text}</Typography>
  </Stack>
)

const Dashboard = () => {
  const [data, setData] = useState(null)
  const [emailMenu, setEmailMenu] = useState(null)
  const [toast, setToast] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    API.get('/admincrm/dashboard-data').then((res) => {
      if (res.status === 200) setData(res.data)
    })
  }, [])

  const copySiteAdminEmails = (includeAll) => {
    setEmailMenu(null)
    API.get(includeAll ? '/admincrm/emailsiteadmins/all' : '/admincrm/emailsiteadmins').then(
      async (res) => {
        if (res.status === 200 && res.data?.length) {
          await navigator.clipboard.writeText(res.data.join(','))
          setToast(`${res.data.length} email address${res.data.length === 1 ? '' : 'es'} copied`)
        } else {
          setToast('No email addresses found')
        }
      },
    )
  }

  const leads = data?.leads
  const followUpsDue = leads?.followUpsDue ?? []
  // The server sorts open commercial usage actions high priority first; the
  // high-priority ones (Enterprise+ upgrade required) lead the whole list.
  const usageActions = (data?.commercialUsageActions ?? []).map(usageActionRow)
  const attention = [
    ...usageActions.filter((row) => row.badge),
    ...(data?.paymentAttention ?? []).map((tenant) => ({
      key: `pay-${tenant.tenantId}`,
      tone: '#E32525',
      title: tenant.name,
      detail: `Subscription payment issue (${tenant.status.replace('_', ' ')})`,
      to: `/tenants/${tenant.tenantId}`,
    })),
    ...(data?.expiringSoon ?? []).map((tenant) => ({
      key: `exp-${tenant.tenantId}`,
      tone: tenant.expired ? '#E32525' : '#F28B1F',
      title: tenant.name,
      detail: `Subscription ${tenant.expired ? 'expired' : 'renews'} ${fromNow(tenant.expiry)} (${formatDate(tenant.expiry)})`,
      to: `/tenants/${tenant.tenantId}`,
    })),
    ...(data?.cancelling ?? []).map((tenant) => ({
      key: `can-${tenant.tenantId}`,
      tone: '#F28B1F',
      title: tenant.name,
      detail: `Cancelling · ends ${formatDate(tenant.cancellationDate)}`,
      to: `/tenants/${tenant.tenantId}`,
    })),
    ...usageActions.filter((row) => !row.badge),
    // Lower-key information: a month still open that is currently over an
    // allowance. Nothing is asked of staff until the month is finalised.
    ...(data?.aboveAllowanceThisMonth ?? []).map((row) => ({
      key: `above-${row.tenantId}-${row.period}`,
      tone: '#64748B',
      icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />,
      title: `${row.name} - above allowance in ${formatPeriod(row.period)}`,
      detail: [usageFigures(row), 'month not finalised yet - no action required']
        .filter(Boolean)
        .join(' · '),
      to: `/tenants/${row.tenantId}?tab=usage`,
    })),
  ]

  const tierCaption = data?.tierCounts
    ? Object.entries(data.tierCounts)
        .filter(([, count]) => count > 0)
        .map(([tier, count]) => `${count} ${TIER_NAMES[tier] ?? `Tier ${tier}`}`)
        .join(' · ')
    : null

  const stageCaption = leads?.byStage
    ? Object.entries(leads.byStage)
        .filter(([, count]) => count > 0)
        .map(([stage, count]) => `${count} ${stageMeta(stage).label.toLowerCase()}`)
        .join(' · ')
    : null

  return (
    <Stack gap={2.5}>
      <PageHeader
        eyebrow={moment().format('dddd D MMMM YYYY')}
        title="Dashboard"
        subtitle="Customers, revenue and the sales pipeline at a glance."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={(event) => setEmailMenu(event.currentTarget)}
            >
              Site admin emails
            </Button>
            <Menu anchorEl={emailMenu} open={!!emailMenu} onClose={() => setEmailMenu(null)}>
              <MenuItem onClick={() => copySiteAdminEmails(false)}>
                Copy subscribed tenants only
              </MenuItem>
              <MenuItem onClick={() => copySiteAdminEmails(true)}>Copy all tenants</MenuItem>
            </Menu>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/new')}>
              New lead
            </Button>
          </>
        }
      />

      {data?.safetyIncidents?.open > 0 && (
        <Alert
          severity={data.safetyIncidents.unacknowledged ? 'error' : 'warning'}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                navigate(
                  data.safetyIncidents.unacknowledged
                    ? '/safety-incidents?filter=unacknowledged'
                    : '/safety-incidents',
                )
              }
            >
              Open log
            </Button>
          }
        >
          {data.safetyIncidents.unacknowledged
            ? `${data.safetyIncidents.unacknowledged} clinical safety incident${data.safetyIncidents.unacknowledged === 1 ? '' : 's'} awaiting acknowledgement (target: one working day).`
            : `${data.safetyIncidents.open} clinical safety incident${data.safetyIncidents.open === 1 ? '' : 's'} open.`}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} lg={2}>
          <StatTile
            label="Active tenants"
            value={data?.activeTenantCount}
            caption={tierCaption || 'Paying subscribers'}
            icon={<BusinessIcon />}
            tone="#00964C"
            onClick={() => navigate('/tenants')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <StatTile
            label="Monthly revenue"
            value={data ? formatCurrency(data.monthlyRecurringRevenue ?? 0) : undefined}
            caption="Contracted fees, annual ÷ 12"
            icon={<PaymentsIcon />}
            tone="#239AA4"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <StatTile
            label="Open leads"
            value={leads?.open}
            caption={stageCaption || 'Nothing in the pipeline'}
            icon={<InsightsIcon />}
            tone="#268EBB"
            onClick={() => navigate('/sales')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <StatTile
            label="Follow-ups due"
            value={followUpsDue.length}
            caption={
              leads?.staleCount
                ? `${leads.staleCount} lead${leads.staleCount === 1 ? '' : 's'} untouched 30+ days`
                : 'Nothing overdue'
            }
            icon={<EventIcon />}
            tone={followUpsDue.length ? '#E32525' : '#64748B'}
            onClick={() => navigate('/sales?filter=due')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <StatTile
            label="Trialled leads"
            value={leads ? leads.trialled + (data.trialledTenantCount ?? 0) : undefined}
            caption="Organisations that once trialled"
            icon={<HistoryIcon />}
            tone="#557A95"
            onClick={() => navigate('/sales?filter=trialled')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <StatTile
            label="Not subscribed"
            value={data?.notSubscribedTenantCount}
            caption="Tenant records awaiting cleanup"
            icon={<CleaningServicesIcon />}
            tone="#F28B1F"
            onClick={() => navigate('/tenants?filter=notSubscribed')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Stack gap={2}>
            <SectionCard
              title="Needs attention"
              subtitle="Payment problems, renewals and commercial usage reviews"
              dense
            >
              {!data ? (
                <EmptyRow text="Loading…" />
              ) : attention.length === 0 ? (
                <EmptyRow
                  icon={<CheckCircleOutlineIcon sx={{ color: 'anGreen.main' }} />}
                  text="All quiet - no tenants need attention right now."
                />
              ) : (
                attention.map((item) => (
                  <AttentionRow
                    key={item.key}
                    tone={item.tone}
                    title={item.title}
                    detail={item.detail}
                    badge={item.badge}
                    icon={item.icon}
                    onOpen={() => navigate(item.to)}
                  />
                ))
              )}
            </SectionCard>

            <SectionCard title="Recently added leads" dense>
              {!leads ? (
                <EmptyRow text="Loading…" />
              ) : leads.recent.length === 0 ? (
                <EmptyRow text="No open leads yet - add one to get started." />
              ) : (
                leads.recent.map((lead) => (
                  <LeadRow
                    key={lead.leadId}
                    lead={lead}
                    onOpen={() => navigate(`/sales/${lead.leadId}`)}
                    trailing={
                      <Stack alignItems="flex-end" gap={0.25}>
                        <StageChip stage={lead.stage} />
                        <Typography variant="caption" color="text.disabled">
                          {fromNow(lead.createdAt)}
                        </Typography>
                      </Stack>
                    }
                  />
                ))
              )}
            </SectionCard>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack gap={2}>
            <SectionCard
              title="Follow-ups"
              subtitle="Due now, then what's coming up"
              dense
              action={
                <Button size="small" onClick={() => navigate('/sales?filter=due')}>
                  Pipeline
                </Button>
              }
            >
              {!leads ? (
                <EmptyRow text="Loading…" />
              ) : followUpsDue.length === 0 && leads.upcomingFollowUps.length === 0 ? (
                <EmptyRow
                  icon={<CheckCircleOutlineIcon sx={{ color: 'anGreen.main' }} />}
                  text="No follow-ups scheduled. Set one from a lead's page."
                />
              ) : (
                <>
                  {[...followUpsDue, ...leads.upcomingFollowUps].map((lead) => {
                    const state = followUpState(lead.nextFollowUpAt)
                    return (
                      <LeadRow
                        key={lead.leadId}
                        lead={lead}
                        onOpen={() => navigate(`/sales/${lead.leadId}`)}
                        trailing={
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: state?.color, whiteSpace: 'nowrap' }}
                          >
                            {state?.key === 'upcoming' ? formatDate(lead.nextFollowUpAt) : state?.label}
                          </Typography>
                        }
                      />
                    )
                  })}
                </>
              )}
            </SectionCard>

            <SectionCard title="Quick links" dense>
              {[
                {
                  label: 'PM2 monitor',
                  icon: <MonitorHeartIcon />,
                  onClick: () =>
                    window.open(
                      'https://app.pm2.io/bucket/666f86927a494a3562be590f/backend/overview/servers',
                      '_blank',
                    ),
                },
                { label: 'Email campaigns', icon: <CampaignIcon />, onClick: () => navigate('/campaigns') },
                { label: 'Maintenance mode', icon: <BuildIcon />, onClick: () => navigate('/maintenance') },
                { label: 'Referral codes', icon: <CardGiftcardIcon />, onClick: () => navigate('/referral-codes') },
              ].map((link) => (
                <Stack
                  key={link.label}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  component="button"
                  type="button"
                  onClick={link.onClick}
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    border: 0,
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    px: 2,
                    py: 1.25,
                    color: 'text.primary',
                    borderBottom: '1px solid #EEF2F7',
                    '&:last-of-type': { borderBottom: 0 },
                    '&:hover': { bgcolor: '#F8FAFC' },
                    '& svg': { fontSize: 20, color: 'text.secondary' },
                  }}
                >
                  {link.icon}
                  <Typography variant="body2" fontWeight={600} flex={1} color="text.primary">
                    {link.label}
                  </Typography>
                  <ChevronRightIcon />
                </Stack>
              ))}
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Stack>
  )
}

export default Dashboard
